import { useTranslation, useDirection } from '@eti/providers';
import { Card, Inline, Notice, Stack, Text, textVariants } from '@etg/wings';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { useRef } from 'react';
import { useSecondBaggage } from '../utils/useSecondBag';
import { useCheckedBag } from '../utils/useCheckedBag';
import SecondBagApplySelectionToAll from '../second-bag-only-purchasable/containers/SecondBagApplySelectionToAllContainer';
import { CHECK_IN_BAGGAGE, SECOND_BAGGAGE } from '../../../products';
import PersuasionModalProvider from '../../../common/context/PersuasionModalContext';
import { generateReadMore } from '../../../common/utils/readMore';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import SecondBagCheckBox from '../second-bag-only-purchasable/containers/SecondBagCheckBoxContainer';
import { getIncludedBaggagePiecesWeightPrimaryFlow, checkedAndSecondBagOptionData } from '../utils/checkInBaggageUtils';
import BaggageSectionHeader from '../../common/components/BaggageSectionHeader';
import BaggageIllustration from '../../common/components/BaggageIllustration';
import BaggageStatus from '../../common/components/BaggageStatus';
import BaggageSectionDescription from '../../common/components/BaggageSectionDescription';
import BaggageSelection from '../../common/components/BaggageSelection';
import ApplySelectionToAllCheckbox from '../../common/components/ApplySelectionToAllCheckbox';
import useBaggageVisibilityTracker from '../../common/hooks/useBaggageVisibilityTracker';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const generalTexts = {
  persuasionContent: 'Product.CheckInBaggage.CD.Persuasion.Content',
  additionalInformation: 'checkInBaggageHeaderAdditionalInformation',
  readMoreContent: 'checkInBaggageReadMoreModalContent',
  abstract: 'checkInBaggageHeaderDescription',
  title: 'checkInBaggageHeaderTitle',
  notAvailableBaggageDescription: 'Product.CheckInBaggage.Not.Included.Or.Sellable.Text',
  titleChoiceNo: 'noCheckInBaggageLabel',
  notIncludedContent: 'Product.CheckInBaggage.Not.Included.Or.Sellable.Text',
  notIncludedBaggageStatusLabel: 'TravelerDetails.Status.Unavailable',
  includedBaggageStatusLabel: 'TravelerDetails.Status.Included',
  sameChoiceForAllLabel: 'Product.CheckInBaggage.SelectSameChoiceForAll',
  pricePerBoundLabel: 'Products.Price.Label.PerBounds'
};
const baggageCardStyle = css`
  border: 1px solid ${etiColors.grayScale[4]};
  border-radius: 3px;
  margin-block-end: 16px;
`;
const expressionWrapper = css`
  padding-block-start: 16px;
`;
const productSection = css`
  flex-wrap: nowrap;
`;
const includedStatus = css`
  justify-content: center;
`;
const notAvailableBaggageStatusTextStyle = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const baggageSelectionStyles = css`
  margin-top: 12px;
  width: 85%;

  @media ${mediaQueries.small.only} {
    margin-top: 0;
    width: 80%;
  }
`;
const CheckInBaggagePrimaryFlow = _ref => {
  let {
    bundleData,
    firstTravelerSelectionValue,
    form,
    includedBaggage,
    isBaggageAvailableOrIncluded,
    order,
    orderSecondBag,
    onChange,
    onChangeAll,
    productName,
    productPosition,
    readMore = {
      isEnabled: true
    },
    shouldApplySelectionToAll,
    travelerNumber,
    totalBaggageQuantity
  } = _ref;
  const {
    t
  } = useTranslation();
  const rootElement = useRef(null);
  const {
    notIncludedContent,
    titleChoiceNo,
    readMoreContent,
    title,
    notIncludedBaggageStatusLabel,
    includedBaggageStatusLabel,
    abstract,
    sameChoiceForAllLabel,
    persuasionContent
  } = getTranslatedTexts(generalTexts, t);
  const {
    isRTL
  } = useDirection();
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });

  // Checked-Bag Data
  const {
    checkedBagNumberOfPieces,
    checkedBagWeight,
    checkedBagWeightUnit,
    isInfant,
    isFirstTraveler,
    isCheckedBaggageIncludedInTrip,
    traveler,
    travelers,
    isCheckedBagPreselected
  } = useCheckedBag({
    travelerNumber,
    order,
    bundleData,
    includedBaggage
  });
  useBaggageVisibilityTracker({
    isBaggageAvailableOrIncluded,
    includedBaggageInTrip: isCheckedBaggageIncludedInTrip,
    productName,
    rootElement,
    productPosition
  });
  const availableForPurchase = isBaggageAvailableOrIncluded && !isCheckedBaggageIncludedInTrip;
  const notAvailableForPurchase = !isBaggageAvailableOrIncluded && !isCheckedBaggageIncludedInTrip;

  // Second-Bag Data
  const {
    secondBagTraveler,
    secondBagTravelers,
    weightOfSecondBag,
    piecesOfSecondBag,
    weightUnitOfSecondBag
  } = useSecondBaggage({
    travelerNumber,
    orderSecondBag
  });
  const {
    baggageContents
  } = checkedAndSecondBagOptionData({
    checkedBagWeight,
    checkedBagWeightUnit,
    checkedBagNumberOfPieces,
    traveler,
    isBaggageAvailableOrIncluded,
    titleChoiceNo,
    secondBagTraveler,
    secondBagProductId: SECOND_BAGGAGE.id,
    weightOfSecondBag,
    piecesOfSecondBag,
    weightUnitOfSecondBag,
    t
  });
  return isInfant ? /*#__PURE__*/_jsx(Text, {
    as: "p",
    "data-testid": "notAvailableBaggage_infants",
    style: {
      color: '#262626',
      paddingBottom: '24px'
    },
    variant: textVariants._14,
    children: t('Product.Infant.Note')
  }) : /*#__PURE__*/_jsx("div", {
    ref: rootElement,
    children: /*#__PURE__*/_jsxs(Card, {
      className: cx('travelerDetailsSection', baggageCardStyle),
      children: [/*#__PURE__*/_jsx(BaggageSectionHeader, {
        productName: productName,
        readMoreModal: generateReadMore(readMore, readMoreContent),
        title: title,
        travelerId: (traveler === null || traveler === void 0 ? void 0 : traveler.id) || ''
      }), /*#__PURE__*/_jsxs(Stack, {
        className: expressionWrapper,
        "data-testid": `travelerDetails-productSection-product-${productName}-body-${traveler === null || traveler === void 0 ? void 0 : traveler.id}`,
        children: [/*#__PURE__*/_jsx(BaggageSectionDescription, {
          description: t('Product.CheckedBaggagePerTraveler.Description.Label'),
          productName: productName,
          travelerId: (traveler === null || traveler === void 0 ? void 0 : traveler.id) || ''
        }), /*#__PURE__*/_jsxs(Inline, {
          className: productSection,
          children: [/*#__PURE__*/_jsx(BaggageIllustration, {
            isRTL: isRTL,
            productName: productName
          }), notAvailableForPurchase && /*#__PURE__*/_jsx(BaggageStatus, {
            className: notAvailableBaggageStatusTextStyle,
            dataTestId: "notAvailableBaggage-status",
            description: notIncludedContent,
            statusLabel: notIncludedBaggageStatusLabel,
            useNotice: true,
            variant: "caution"
          }), isCheckedBaggageIncludedInTrip && (orderSecondBag && Object.keys(orderSecondBag).length !== 0 ? /*#__PURE__*/_jsxs(Stack, {
            className: includedStatus,
            children: [/*#__PURE__*/_jsx(BaggageStatus, {
              dataTestId: "includedBaggage-status",
              description: getIncludedBaggagePiecesWeightPrimaryFlow(includedBaggage, bundleData, t),
              statusLabel: includedBaggageStatusLabel,
              variant: "positive"
            }), /*#__PURE__*/_jsx(SecondBagCheckBox, {
              abstract: abstract,
              firstTravelerSelectionValue: firstTravelerSelectionValue,
              form: form,
              isFirstTraveler: isFirstTraveler,
              orderSecondBag: orderSecondBag,
              productName: productName,
              secondBagTraveler: secondBagTraveler,
              secondBagTravelers: secondBagTravelers,
              travelerNumber: travelerNumber
            })]
          }) : /*#__PURE__*/_jsx(BaggageStatus, {
            dataTestId: "includedBaggage-status",
            description: getIncludedBaggagePiecesWeightPrimaryFlow(includedBaggage, bundleData, t),
            statusLabel: includedBaggageStatusLabel,
            variant: "positive"
          })), availableForPurchase ? /*#__PURE__*/_jsxs(Stack, {
            className: baggageSelectionStyles,
            spacing: 8,
            children: [/*#__PURE__*/_jsx(PersuasionModalProvider, {
              children: /*#__PURE__*/_jsx(BaggageSelection, {
                baggageOptions: baggageContents,
                onChange: onChange,
                order: order,
                persuasionContent: persuasionContent,
                persuasionTitle: title,
                productId: CHECK_IN_BAGGAGE.id,
                productName: productName,
                productPosition: productPosition,
                radioGroupScreenReaderDescription: t('Product.CheckedBaggagePerTraveler.Description.Label'),
                rootElement: rootElement,
                secondBagProductId: piecesOfSecondBag ? SECOND_BAGGAGE.id : undefined,
                shouldApplySelectionToAll: shouldApplySelectionToAll,
                totalBaggageQuantity: totalBaggageQuantity,
                travelerId: traveler.id
              })
            }), abstract && !isCheckedBagPreselected && /*#__PURE__*/_jsx(Notice, {
              variant: "promotional",
              children: /*#__PURE__*/_jsx(Text, {
                asHtml: true,
                variant: textVariants._14_BOLD,
                children: abstract
              })
            })]
          }) : null]
        }), availableForPurchase && isFirstTraveler && travelers.length > 1 && /*#__PURE__*/_jsx(ApplySelectionToAllCheckbox, {
          firstTravelerSelectionValue: firstTravelerSelectionValue,
          label: sameChoiceForAllLabel,
          onChangeAll: onChangeAll,
          productName: productName,
          shouldApplySelectionToAll: shouldApplySelectionToAll
        }), isCheckedBaggageIncludedInTrip && isFirstTraveler && (secondBagTravelers === null || secondBagTravelers === void 0 ? void 0 : secondBagTravelers.length) > 1 && /*#__PURE__*/_jsx(SecondBagApplySelectionToAll, {
          firstTravelerSelectionValue: firstTravelerSelectionValue,
          form: form,
          label: sameChoiceForAllLabel,
          orderSecondBag: orderSecondBag,
          productName: productName,
          secondBagTravelers: secondBagTravelers
        })]
      })]
    })
  });
};
export default CheckInBaggagePrimaryFlow;
//# sourceMappingURL=CheckInBaggagePrimaryFlow.js.map